import { useNavigate, Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, Lock, Target, TrendingUp, Clock4,
  Coffee, ShoppingCart, Wrench, Sparkles, FileText, Code2, Check, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { SiteHeader } from "@/components/SiteHeader";
import { Seo, SITE_URL } from "@/components/Seo";
import { branchen } from "@/content/branchen";

const ICONS = {
  target: Target, shield: ShieldCheck, lock: Lock, trend: TrendingUp,
  clock: Clock4, coffee: Coffee, shop: ShoppingCart, wrench: Wrench, code: Code2,
};

const BRANCHE_ICONS = {
  "e-commerce": ShoppingCart,
  gastronomie: Coffee,
  handwerk: Wrench,
  "saas-agentur": Code2,
  "beauty-wellness": Sparkles,
};

const DISCLAIMER =
  "Hinweis: Gründio erstellt einen automatisierten, KI-gestützten Report als Orientierungshilfe und Denkanstoß – ausdrücklich keine Rechts-, Steuer- oder Unternehmensberatung und keine Erfolgsgarantie.";

export default function ContentLanding({ content }) {
  const nav = useNavigate();
  const startForm = () => nav("/form");

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: content.breadcrumb, item: `${SITE_URL}${content.path}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen">
      <Seo
        title={content.seo.title}
        description={content.seo.description}
        path={content.path}
        keywords={content.seo.keywords}
        noindex
        jsonLd={jsonLd}
      />
      <SiteHeader />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dots opacity-60 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 pt-14 pb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200/80 text-xs uppercase tracking-widest text-slate-600 mb-6 shadow-sm">
            <span className="h-1.5 w-1.5 rounded-full brand-gradient-bg" />
            {content.eyebrow}
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            <span className="text-slate-900">{content.hero.lines[0]} </span>
            <span className="brand-gradient-text">{content.hero.lines[1]}</span>
          </h1>
          <div className="mt-6 space-y-4 max-w-2xl">
            {content.hero.paragraphs.map((p, i) => (
              <p key={i} className="text-base sm:text-lg text-slate-600 leading-relaxed">{p}</p>
            ))}
          </div>
          <div className="mt-8">
            <Button
              data-testid="hero-start-btn"
              onClick={startForm}
              className="pulse-cta h-auto min-h-14 py-3 px-6 sm:px-8 text-sm sm:text-base font-semibold rounded-xl shadow-lg brand-gradient-bg text-white hover:opacity-95 whitespace-normal text-center leading-snug w-full sm:w-auto"
            >
              {content.hero.ctaLabel || "Kostenlosen Score-Check starten"}
              <ArrowRight className="ml-2 h-5 w-5 shrink-0" />
            </Button>
            <p className="mt-3 text-xs text-slate-500 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" style={{ color: "#10C98D" }} /> {content.hero.note || "Score-Tendenz gratis · Report 29 € · anonym & datensparsam"}
            </p>
          </div>
        </div>
      </section>

      {/* SECTIONS */}
      {content.sections.map((sec, si) => (
        <section key={si} className="max-w-5xl mx-auto px-6 pb-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
            {sec.title}
          </h2>
          {sec.intro && <p className="mt-3 text-slate-600 max-w-3xl leading-relaxed">{sec.intro}</p>}
          {sec.paragraphs && (
            <div className="mt-3 space-y-3 max-w-3xl">
              {sec.paragraphs.map((p, i) => (
                <p key={i} className="text-slate-600 leading-relaxed">{p}</p>
              ))}
            </div>
          )}
          {sec.bulletsLead && <p className="mt-4 text-slate-700 font-medium max-w-3xl">{sec.bulletsLead}</p>}
          {sec.bullets && (
            <ul className="mt-4 space-y-2.5 max-w-3xl">
              {sec.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-[15px] text-slate-700">
                  <span className="mt-1 h-5 w-5 rounded-full brand-gradient-bg flex items-center justify-center shrink-0">
                    <Check className="h-3 w-3 text-white" />
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}

          {sec.layout === "numbered" ? (
            <ol className="mt-8 space-y-5">
              {sec.items.map((it, i) => (
                <li key={i} className="relative pl-14 bg-white rounded-2xl border border-slate-200/70 p-5">
                  <div
                    className="absolute left-5 top-5 h-9 w-9 rounded-xl text-white font-bold flex items-center justify-center brand-gradient-bg shadow-md"
                    style={{ fontFamily: "Outfit" }}
                  >
                    {i + 1}
                  </div>
                  <div className="pl-8">
                    <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>{it.title}</h3>
                    <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{it.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          ) : sec.layout === "comparison" ? (
            <div className="mt-8 grid md:grid-cols-2 gap-5">
              <div className="rounded-2xl bg-white border border-slate-200/70 p-6">
                <div className="text-sm font-semibold text-slate-500 uppercase tracking-widest mb-4">{sec.left.label}</div>
                <ul className="space-y-3">
                  {sec.left.points.map((p, i) => (
                    <li key={i} className="flex items-start gap-3 text-[15px] text-slate-600">
                      <span className="mt-0.5 h-5 w-5 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                        <X className="h-3 w-3 text-slate-400" />
                      </span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl bg-white border-2 border-emerald-300 p-6 shadow-lg">
                <div className="text-sm font-semibold uppercase tracking-widest mb-4 brand-gradient-text">{sec.right.label}</div>
                <ul className="space-y-3">
                  {sec.right.points.map((p, i) => (
                    <li key={i} className="flex items-start gap-3 text-[15px] text-slate-700">
                      <span className="mt-0.5 h-5 w-5 rounded-full brand-gradient-bg flex items-center justify-center shrink-0">
                        <Check className="h-3 w-3 text-white" />
                      </span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : sec.layout === "faq" ? (
            <div className="mt-8 space-y-3 max-w-3xl">
              {sec.items.map((it, i) => (
                <details key={i} className="group bg-white rounded-2xl border border-slate-200/70 p-5">
                  <summary className="cursor-pointer font-semibold text-slate-900 list-none flex justify-between items-center">
                    <span style={{ fontFamily: "Outfit" }}>{it.q}</span>
                    <span className="text-emerald-600 group-open:rotate-45 transition-transform">＋</span>
                  </summary>
                  <p className="text-sm text-slate-600 mt-3 leading-relaxed">{it.a}</p>
                </details>
              ))}
            </div>
          ) : sec.layout === "cards" ? (
            <div className={`mt-8 grid gap-5 ${sec.cols === 2 ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
              {sec.items.map((it, i) => {
                const Icon = ICONS[it.icon] || Sparkles;
                return (
                  <div key={i} className="rounded-2xl bg-white border border-slate-200/70 p-6 hover:border-emerald-300 hover:shadow-lg transition-all">
                    <div className="h-11 w-11 rounded-xl flex items-center justify-center mb-4 bg-emerald-50">
                      <Icon className="h-5 w-5" style={{ color: "#10C98D" }} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>{it.title}</h3>
                    <p className="text-sm text-slate-600 mt-2 leading-relaxed">{it.body}</p>
                  </div>
                );
              })}
            </div>
          ) : sec.layout === "branchen-links" ? (
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {Object.entries(branchen).map(([slug, b]) => {
                const Icon = BRANCHE_ICONS[slug] || Sparkles;
                return (
                  <Link
                    key={slug}
                    to={b.path}
                    data-testid={`branche-card-${slug}`}
                    className="group rounded-2xl bg-white border border-slate-200/70 p-6 hover:border-emerald-300 hover:shadow-lg transition-all flex flex-col"
                  >
                    <div className="h-11 w-11 rounded-xl flex items-center justify-center mb-4 bg-emerald-50">
                      <Icon className="h-5 w-5" style={{ color: "#10C98D" }} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>{b.breadcrumb}</h3>
                    <p className="text-sm text-slate-600 mt-2 leading-relaxed flex-1">{b.hubTeaser || b.seo?.description}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold brand-gradient-text">
                      Branche ansehen <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </Link>
                );
              })}
            </div>
          ) : null}

          {sec.note && <p className="mt-6 text-sm text-slate-500 max-w-3xl leading-relaxed">{sec.note}</p>}
        </section>
      ))}

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        <div className="relative rounded-3xl overflow-hidden">
          <div className="absolute inset-0 bg-slate-900" />
          <div className="absolute -top-32 -right-32 h-72 w-72 rounded-full opacity-30 brand-gradient-bg blur-3xl" />
          <div className="absolute -bottom-32 -left-32 h-72 w-72 rounded-full opacity-25 bg-emerald-400 blur-3xl" />
          <div className="relative p-8 sm:p-10 text-white">
            <div className="text-xs uppercase tracking-widest text-emerald-300 mb-2 flex items-center gap-2">
              <FileText className="h-4 w-4" /> {content.cta.badge}
            </div>
            <h2 className="text-2xl md:text-3xl font-semibold" style={{ fontFamily: "Outfit" }}>{content.cta.title}</h2>
            <p className="text-white/70 mt-3 max-w-2xl leading-relaxed">{content.cta.text}</p>
            <Button
              data-testid="cta-bottom-btn"
              onClick={startForm}
              className="mt-6 h-auto min-h-14 py-3 px-7 rounded-xl font-semibold brand-gradient-bg text-white hover:opacity-95 whitespace-normal text-center leading-snug"
            >
              <Sparkles className="h-5 w-5 mr-2 shrink-0" /> {content.cta.buttonLabel}
            </Button>
            <p className="mt-4 text-xs text-white/50 max-w-2xl">{DISCLAIMER}</p>
          </div>
        </div>
      </section>

      {/* Cross-links */}
      <section className="max-w-5xl mx-auto px-6 pb-16">
        <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Weitere Checks</div>
        <div className="flex flex-wrap gap-3 text-sm">
          <Link to="/geschaeftsidee-testen" className="px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 hover:border-emerald-300">Blinde-Flecken-Check</Link>
          <Link to="/feedback-geschaeftsidee" className="px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 hover:border-emerald-300">Ehrliches Feedback</Link>
          <Link to="/branchen-check" className="px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 hover:border-emerald-300">Branchen-Check</Link>
          <Link to="/gruendungszuschuss" className="px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 hover:border-emerald-300">Gründungszuschuss</Link>
        </div>
      </section>

      <footer className="border-t border-slate-200/70 bg-white/60">
        <div className="max-w-6xl mx-auto px-6 py-8 text-xs text-slate-500 grid sm:grid-cols-2 gap-4 items-start">
          <div className="space-y-2">
            <Logo size="sm" />
            <p className="max-w-md leading-relaxed">
              Gründio bietet eine automatisierte Orientierungshilfe und KI-gestützte Erstbewertung.
              <b> Keine Rechts- oder Steuerberatung</b>.
            </p>
          </div>
          <div className="sm:text-right space-y-2">
            <nav className="flex sm:justify-end items-center gap-4 flex-wrap">
              <Link to="/impressum" className="hover:text-slate-900">Impressum</Link>
              <Link to="/datenschutz" className="hover:text-slate-900">Datenschutz</Link>
              <Link to="/agb" className="hover:text-slate-900">AGB</Link>
            </nav>
            <div>© {new Date().getFullYear()} Gründio · Stand 2026</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
